<template>
  <div class="adhoc-form-root">
  <div v-if="show" class="adhoc-overlay" @click.self="handleClose">
    <div class="adhoc-modal">

      <div class="adhoc-modal-head">
        <div class="adhoc-modal-title">
          <fa-icon :icon="['fas', 'plus']" class="adhoc-modal-icon" />
          Add Ad Hoc Deadline
        </div>
        <button class="adhoc-modal-close" :disabled="saving" @click="handleClose">
          <fa-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <form class="adhoc-form" @submit.prevent="submit">

        <!-- Client search -->
        <div class="adhoc-field" ref="clientField">
          <label class="adhoc-label">Client <span class="adhoc-req">*</span></label>
          <div class="adhoc-search-wrap">
            <fa-icon :icon="['fas', 'search']" class="adhoc-search-icon" />
            <input
              v-model="searchQuery"
              class="adhoc-search-input"
              :class="{ 'is-error': errors.client }"
              type="text"
              placeholder="Search client name…"
              autocomplete="off"
              @input="onSearchInput"
              @focus="dropdownOpen = true"
              @keydown.escape="dropdownOpen = false"
            />
            <div v-if="selectedClient" class="adhoc-search-clear" @click="clearClient">
              <fa-icon :icon="['fas', 'times']" />
            </div>
          </div>
          <div v-if="dropdownOpen && filteredClients.length" class="adhoc-dropdown">
            <div
              v-for="c in filteredClients"
              :key="c.id"
              class="adhoc-dropdown-item"
              @mousedown.prevent="selectClient(c)"
            >
              <span class="adhoc-dd-name">{{ c.client }}</span>
              <span v-if="c.initial" class="adhoc-dd-initial">{{ c.initial }}</span>
            </div>
          </div>
          <div v-if="dropdownOpen && searchQuery.length >= 1 && !filteredClients.length" class="adhoc-dropdown adhoc-dropdown--empty">
            No clients found
          </div>
          <span v-if="errors.client" class="adhoc-err">{{ errors.client }}</span>
        </div>

        <!-- Deadline date (reuse DateInput) -->
        <div class="adhoc-field">
          <date-input
            v-model="form.date"
            label="Deadline Date"
            :required="true"
          />
          <span v-if="errors.date" class="adhoc-err">{{ errors.date }}</span>
        </div>

        <!-- Task name -->
        <div class="adhoc-field">
          <label class="adhoc-label">Task <span class="adhoc-req">*</span></label>
          <div class="adhoc-input-wrap">
            <input
              v-model="form.task"
              class="adhoc-input"
              :class="{ 'is-error': errors.task }"
              type="text"
              maxlength="15"
              placeholder="e.g. AGM Filing"
              @input="errors.task = ''"
            />
            <span class="adhoc-char-count" :class="{ 'is-warn': form.task.length >= 13 }">
              {{ form.task.length }}/15
            </span>
          </div>
          <span v-if="errors.task" class="adhoc-err">{{ errors.task }}</span>
        </div>

        <div class="adhoc-form-foot">
          <app-button @click="submit" variant="custom" size="md" customBg="#d97706" customHoverBg="#B16105" style="flex:1"><span><fa-icon :icon="['fas', saving ? 'spinner' : 'plus']" :spin="saving" /></span><span>{{ saving ? 'Saving…' : 'Add Deadline' }}</span></app-button>
          <app-button @click="handleClose" :disabled="saving" variant="custom" customBg="#9ca3af" customHoverBg="#6b7280" style="flex:1"><span>Cancel</span></app-button>
        </div>

      </form>
    </div>

  </div>

  <alert-modal
    :show="alert.show"
    :status="alert.status"
    :message="alert.message"
    @close="onAlertClose"
  />
  </div>
</template>

<script>
import DateInput from '@/components/form/DateInput.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import AppButton from './AppButton.vue'

export default {
  name: 'AdHocDeadlineForm',
  components: { DateInput, AlertModal, AppButton },
  props: {
    show: { type: Boolean, default: false },
    clients: { type: Array, default: () => [] },
    context: { type: String, default: 'tax' }
  },
  data () {
    return {
      searchQuery: '',
      selectedClient: null,
      dropdownOpen: false,
      form: { date: '', task: '' },
      errors: { client: '', date: '', task: '' },
      saving: false,
      alert: { show: false, status: 'success', message: '' }
    }
  },
  computed: {
    filteredClients () {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.clients.slice(0, 10)
      return this.clients
        .filter(c => c.client.toLowerCase().includes(q) || (c.initial || '').toLowerCase().includes(q))
        .slice(0, 10)
    }
  },
  watch: {
    show (val) {
      if (val) this.reset()
    }
  },
  mounted () {
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onDocClick (e) {
      if (this.$refs.clientField && !this.$refs.clientField.contains(e.target)) {
        this.dropdownOpen = false
      }
    },
    onSearchInput () {
      this.selectedClient = null
      this.errors.client = ''
      this.dropdownOpen = true
    },
    selectClient (c) {
      this.selectedClient = c
      this.searchQuery = c.client
      this.dropdownOpen = false
      this.errors.client = ''
    },
    clearClient () {
      this.selectedClient = null
      this.searchQuery = ''
      this.errors.client = ''
    },
    validate () {
      let valid = true
      this.errors = { client: '', date: '', task: '' }
      if (!this.selectedClient) {
        this.errors.client = 'Please select a client.'
        valid = false
      }
      if (!this.form.date) {
        this.errors.date = 'Please select a deadline date.'
        valid = false
      }
      const task = this.form.task.trim()
      if (!task) {
        this.errors.task = 'Task name is required.'
        valid = false
      } else if (task.length > 15) {
        this.errors.task = 'Task name must be 15 characters or fewer.'
        valid = false
      }
      return valid
    },
    async submit () {
      if (!this.validate()) return
      this.saving = true

      // Parse day, month, year from DD/MM/YYYY
      const [dd, mm, yyyy] = this.form.date.split('/')
      const month = parseInt(mm)
      const day = parseInt(dd)
      const year = parseInt(yyyy)

      try {
        const token = localStorage.getItem('adminToken')
        const res = await fetch('https://tyng-ca.com.my/api/admin/tasks/save-adhoc-deadline.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            client_id: this.selectedClient.id,
            task_name: this.form.task.trim(),
            deadline_month: month,
            deadline_day: day,
            deadline_year: year,
            context: this.context
          })
        })
        const data = await res.json()
        if (res.status === 401) {
          this.alert = { show: true, status: 'error', message: 'Session expired. Please refresh the page and try again.' }
          return
        }
        if (data && data.success) {
          this.$emit('saved', { clientId: this.selectedClient.id, item: data.item })
          this.alert = { show: true, status: 'success', message: `Ad hoc deadline "${data.item.display_name}" added for ${this.selectedClient.client}.` }
        } else {
          this.alert = { show: true, status: 'error', message: data.message || 'Failed to save. Please try again.' }
        }
      } catch (err) {
        this.alert = { show: true, status: 'error', message: 'An unexpected error occurred.' }
      } finally {
        this.saving = false
      }
    },
    onAlertClose () {
      this.alert.show = false
      if (this.alert.status === 'success') {
        this.reset()
        this.$emit('close')
      }
    },
    reset () {
      this.searchQuery = ''
      this.selectedClient = null
      this.dropdownOpen = false
      this.form = { date: '', task: '' }
      this.errors = { client: '', date: '', task: '' }
      this.saving = false
      this.alert = { show: false, status: 'success', message: '' }
    },
    handleClose () {
      if (this.saving) return
      this.reset()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.adhoc-form-foot {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
</style>
